import { StorageTransferOrderStatus, StorageTransferOrderStatusLabels } from '@navio/nsdk';

type TransferOrderStatus = (typeof StorageTransferOrderStatus)[keyof typeof StorageTransferOrderStatus];

/**
 * Action labels for status-related buttons. Only cancel remains as a
 * staff/worker action; IN_PROGRESS / COMPLETED are driven by routes.
 */
export const TRANSFER_ORDER_STATUS_ACTION_LABEL: Record<TransferOrderStatus, string> = {
  DRAFT: '',
  CREATED: '',
  IN_PROGRESS: '',
  COMPLETED: '',
  CANCELLED: 'Cancel order'
};

/**
 * Non-terminal "active" transfer-order statuses. Mirrors the backend
 * `STORAGE_TRANSFER_ORDER_ACTIVE_STATUSES`: orders that have been sent
 * to recipients and pre-authorize cross-facility wheel-set moves.
 * DRAFT is non-terminal but deliberately excluded — drafts haven't been
 * communicated yet and don't unblock cross-facility moves.
 */
export const TRANSFER_ORDER_ACTIVE_STATUSES: readonly TransferOrderStatus[] = [
  StorageTransferOrderStatus.CREATED,
  StorageTransferOrderStatus.IN_PROGRESS
] as const;

/**
 * Default partner list filter: sent/in-flight orders only. Drafts are
 * composed in the create stepper and are hidden from the list by default.
 */
export const TRANSFER_ORDER_PARTNER_LIST_DEFAULT_STATUSES: readonly TransferOrderStatus[] = [
  StorageTransferOrderStatus.CREATED,
  StorageTransferOrderStatus.IN_PROGRESS
] as const;

/**
 * Non-terminal transfer-order statuses. Mirrors the backend
 * `STORAGE_TRANSFER_ORDER_NON_TERMINAL_STATUSES`: orders still in flight
 * whose items can be edited. DRAFT is non-terminal but does not reserve
 * wheel sets.
 */
const TRANSFER_ORDER_NON_TERMINAL_STATUSES: readonly TransferOrderStatus[] = [
  StorageTransferOrderStatus.DRAFT,
  StorageTransferOrderStatus.CREATED,
  StorageTransferOrderStatus.IN_PROGRESS
] as const;

/**
 * Whether any of the given storage object groups sits on an active
 * transfer order (CREATED or IN_PROGRESS). Duck-typed so web and mobile
 * record shapes can share it. Used to suppress the super-user override
 * hint — when an order is already in flight, cross-facility movement
 * is the normal flow.
 */
export const hasAnyActiveTransferOrder = (
  groups: readonly { upcomingTransferOrders?: readonly { status: { value: TransferOrderStatus } }[] | null }[]
): boolean =>
  groups.some((group) =>
    (group.upcomingTransferOrders ?? []).some((order) => TRANSFER_ORDER_ACTIVE_STATUSES.includes(order.status.value))
  );

/**
 * Destination facility ids of a group's active (CREATED or IN_PROGRESS)
 * transfer orders. Duck-typed so web and mobile record shapes can share
 * it. Drop-in for the `activeTransferOrderDestinationFacilityIds` field
 * on `AllowedFacilityInput`. Centralizing this keeps the status filter
 * from drifting between callers.
 */
export const getActiveTransferOrderDestinationFacilityIds = (group: {
  upcomingTransferOrders?:
    | readonly { status: { value: TransferOrderStatus }; destinationStorageFacility: { id: number } }[]
    | null;
}): number[] =>
  (group.upcomingTransferOrders ?? [])
    .filter((order) => TRANSFER_ORDER_ACTIVE_STATUSES.includes(order.status.value))
    .map((order) => order.destinationStorageFacility.id);

export const isTransferOrderCancellable = (status: TransferOrderStatus): boolean =>
  status !== StorageTransferOrderStatus.COMPLETED && status !== StorageTransferOrderStatus.CANCELLED;

export const areTransferOrderItemsEditable = (status: TransferOrderStatus): boolean =>
  TRANSFER_ORDER_NON_TERMINAL_STATUSES.includes(status);

export const isTransferOrderDraft = (status: TransferOrderStatus): boolean =>
  status === StorageTransferOrderStatus.DRAFT;

export const getTransferOrderStatusLabel = (status: TransferOrderStatus): string =>
  StorageTransferOrderStatusLabels[status];

/**
 * Generic badge intent for a transfer-order status. Apps map this to their
 * own badge variant union (web `BadgeVariant` vs mobile `NativeNoddiBadgeVariant`).
 */
type TransferOrderStatusBadgeIntent = 'success' | 'destructive' | 'outline' | 'info';

export const getTransferOrderStatusBadgeIntent = (status: TransferOrderStatus): TransferOrderStatusBadgeIntent => {
  switch (status) {
    case StorageTransferOrderStatus.DRAFT:
      return 'outline';
    case StorageTransferOrderStatus.CREATED:
      return 'outline';
    case StorageTransferOrderStatus.IN_PROGRESS:
      return 'info';
    case StorageTransferOrderStatus.COMPLETED:
      return 'success';
    case StorageTransferOrderStatus.CANCELLED:
      return 'destructive';
    default:
      return 'outline';
  }
};

/**
 * Semantic intent for the colored dot rendered next to each entry in a
 * transfer-order status-history timeline. Each app maps intent to its own
 * design-token classes, keeping the status → meaning mapping in one place.
 */
export type TransferOrderStatusDotIntent = 'primary' | 'success' | 'destructive';

export const getTransferOrderStatusDotIntent = (status: TransferOrderStatus): TransferOrderStatusDotIntent => {
  switch (status) {
    case StorageTransferOrderStatus.COMPLETED:
      return 'success';
    case StorageTransferOrderStatus.CANCELLED:
      return 'destructive';
    case StorageTransferOrderStatus.DRAFT:
    case StorageTransferOrderStatus.CREATED:
    case StorageTransferOrderStatus.IN_PROGRESS:
    default:
      return 'primary';
  }
};
